// Security utilities for password hashing and validation

// ═══════════════════════════════════════════════════════
// PASSWORD HASHING
// ═══════════════════════════════════════════════════════

// Enhanced hash with multiple rounds (for client-side - in production use bcrypt on server)
export async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder()
  // Use PBKDF2-like approach with multiple SHA-256 rounds
  let data = encoder.encode(password + 'operines_salt_2024_v2')
  for (let i = 0; i < 10000; i++) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    data = new Uint8Array(hashBuffer)
  }
  const hashArray = Array.from(new Uint8Array(data))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

// Verify password against hash
export async function verifyPassword(
  password: string,
  hash: string,
): Promise<boolean> {
  const passwordHash = await hashPassword(password)
  // Timing-safe comparison to prevent timing attacks
  if (passwordHash.length !== hash.length) return false
  let result = 0
  for (let i = 0; i < passwordHash.length; i++) {
    result |= passwordHash.charCodeAt(i) ^ hash.charCodeAt(i)
  }
  return result === 0
}

// ═══════════════════════════════════════════════════════
// BRUTE FORCE PROTECTION & RATE LIMITING
// ═══════════════════════════════════════════════════════

interface RateLimitEntry {
  attempts: number
  firstAttempt: number
  lockedUntil: number | null
}

const RATE_LIMIT_WINDOW = 15 * 60 * 1000 // 15 minutes
const MAX_ATTEMPTS = 5
const LOCKOUT_DURATION = 30 * 60 * 1000 // 30 minutes lockout

export function checkRateLimit(key: string): {
  allowed: boolean
  remainingAttempts: number
  lockoutMinutes: number
} {
  const storageKey = `rl_${key}`
  const now = Date.now()

  try {
    const stored = sessionStorage.getItem(storageKey)
    if (!stored) {
      return {
        allowed: true,
        remainingAttempts: MAX_ATTEMPTS,
        lockoutMinutes: 0,
      }
    }

    const entry: RateLimitEntry = JSON.parse(stored)

    // Check if locked out
    if (entry.lockedUntil && now < entry.lockedUntil) {
      const minutesLeft = Math.ceil((entry.lockedUntil - now) / 60000)
      return {
        allowed: false,
        remainingAttempts: 0,
        lockoutMinutes: minutesLeft,
      }
    }

    // Reset if window expired
    if (now - entry.firstAttempt > RATE_LIMIT_WINDOW) {
      sessionStorage.removeItem(storageKey)
      return {
        allowed: true,
        remainingAttempts: MAX_ATTEMPTS,
        lockoutMinutes: 0,
      }
    }

    const remaining = MAX_ATTEMPTS - entry.attempts
    return {
      allowed: remaining > 0,
      remainingAttempts: Math.max(0, remaining),
      lockoutMinutes: 0,
    }
  } catch {
    return { allowed: true, remainingAttempts: MAX_ATTEMPTS, lockoutMinutes: 0 }
  }
}

export function recordAttempt(key: string, success: boolean): void {
  if (success) {
    sessionStorage.removeItem(`rl_${key}`)
    return
  }

  const storageKey = `rl_${key}`
  const now = Date.now()

  try {
    const stored = sessionStorage.getItem(storageKey)
    let entry: RateLimitEntry = stored
      ? JSON.parse(stored)
      : { attempts: 0, firstAttempt: now, lockedUntil: null }

    // Reset if window expired
    if (now - entry.firstAttempt > RATE_LIMIT_WINDOW) {
      entry = { attempts: 0, firstAttempt: now, lockedUntil: null }
    }

    entry.attempts += 1

    // Lock out if too many attempts
    if (entry.attempts >= MAX_ATTEMPTS) {
      entry.lockedUntil = now + LOCKOUT_DURATION
    }

    sessionStorage.setItem(storageKey, JSON.stringify(entry))
  } catch {
    // Silently fail
  }
}

// ═══════════════════════════════════════════════════════
// INPUT SANITIZATION (XSS Prevention)
// ═══════════════════════════════════════════════════════

export function sanitizeInput(input: string): string {
  if (!input) return ''
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim()
}

export function sanitizeEmail(email: string): string {
  if (!email) return ''
  // Remove any non-email characters and trim
  return email
    .toLowerCase()
    .trim()
    .replace(/[^\w.@+-]/g, '')
}

export function sanitizePhone(phone: string): string {
  if (!phone) return ''
  // Only allow digits, +, -, spaces
  return phone.replace(/[^\d+\-\s]/g, '').trim()
}

// ═══════════════════════════════════════════════════════
// CSRF TOKEN
// ═══════════════════════════════════════════════════════

export function generateCSRFToken(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function getOrCreateCSRFToken(): string {
  let token = sessionStorage.getItem('csrf_token')
  if (!token) {
    token = generateCSRFToken()
    sessionStorage.setItem('csrf_token', token)
  }
  return token
}

// ═══════════════════════════════════════════════════════
// SESSION MANAGEMENT
// ═══════════════════════════════════════════════════════

const SESSION_DURATION = 24 * 60 * 60 * 1000 // 24 hours

export function createSession(userId: string): string {
  const sessionId = generateCSRFToken()
  const session = {
    id: sessionId,
    userId,
    createdAt: Date.now(),
    expiresAt: Date.now() + SESSION_DURATION,
  }
  sessionStorage.setItem('app_session', JSON.stringify(session))
  return sessionId
}

export function validateSession(): boolean {
  try {
    const stored = sessionStorage.getItem('app_session')
    if (!stored) return false
    const session = JSON.parse(stored)
    if (Date.now() > session.expiresAt) {
      sessionStorage.removeItem('app_session')
      return false
    }
    return true
  } catch {
    return false
  }
}

export function destroySession(): void {
  sessionStorage.removeItem('app_session')
  sessionStorage.removeItem('csrf_token')
}

// ═══════════════════════════════════════════════════════
// SECURE STORAGE (Encrypted localStorage)
// ═══════════════════════════════════════════════════════

const STORAGE_KEY_PREFIX = 'sec_'

function getDeviceFingerprint(): string {
  const nav = navigator
  const fp = `${nav.userAgent}|${nav.language}|${screen.width}x${screen.height}|${new Date().getTimezoneOffset()}`
  // Simple hash of fingerprint
  let hash = 0
  for (let i = 0; i < fp.length; i++) {
    const char = fp.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36)
}

export function secureStore(key: string, data: any): void {
  try {
    const json = JSON.stringify(data)
    const encoded = btoa(encodeURIComponent(json))
    localStorage.setItem(STORAGE_KEY_PREFIX + key, encoded)
  } catch {
    // Fallback to plain storage
    localStorage.setItem(key, JSON.stringify(data))
  }
}

export function secureRetrieve(key: string): any {
  try {
    const encoded = localStorage.getItem(STORAGE_KEY_PREFIX + key)
    if (!encoded) {
      // Try fallback plain storage
      const plain = localStorage.getItem(key)
      return plain ? JSON.parse(plain) : null
    }
    const json = decodeURIComponent(atob(encoded))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export function secureRemove(key: string): void {
  localStorage.removeItem(STORAGE_KEY_PREFIX + key)
  localStorage.removeItem(key)
}

// ═══════════════════════════════════════════════════════
// PASSWORD VALIDATION
// ═══════════════════════════════════════════════════════

export interface PasswordValidation {
  isValid: boolean
  errors: string[]
  strength: 'weak' | 'medium' | 'strong'
}

export function validatePassword(
  password: string,
  isRTL: boolean = true,
): PasswordValidation {
  const errors: string[] = []
  let score = 0

  if (password.length < 8) {
    errors.push(
      isRTL
        ? 'يجب أن تكون كلمة المرور 8 أحرف على الأقل'
        : 'Password must be at least 8 characters',
    )
  } else {
    score += 1
  }

  if (!/[A-Z]/.test(password)) {
    errors.push(
      isRTL
        ? 'يجب أن تحتوي على حرف كبير واحد على الأقل'
        : 'Must contain at least one uppercase letter',
    )
  } else {
    score += 1
  }

  if (!/[a-z]/.test(password)) {
    errors.push(
      isRTL
        ? 'يجب أن تحتوي على حرف صغير واحد على الأقل'
        : 'Must contain at least one lowercase letter',
    )
  } else {
    score += 1
  }

  if (!/[0-9]/.test(password)) {
    errors.push(
      isRTL
        ? 'يجب أن تحتوي على رقم واحد على الأقل'
        : 'Must contain at least one number',
    )
  } else {
    score += 1
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push(
      isRTL
        ? 'يجب أن تحتوي على رمز خاص واحد على الأقل (!@#$%^&*)'
        : 'Must contain at least one special character (!@#$%^&*)',
    )
  } else {
    score += 1
  }

  // Check for common passwords
  const commonPasswords = [
    'password',
    '12345678',
    'qwerty123',
    'admin123',
    'letmein',
    'welcome1',
  ]
  if (commonPasswords.some((cp) => password.toLowerCase().includes(cp))) {
    errors.push(isRTL ? 'كلمة المرور شائعة جداً' : 'Password is too common')
    score = Math.max(0, score - 2)
  }

  let strength: 'weak' | 'medium' | 'strong' = 'weak'
  if (score >= 5) {
    strength = 'strong'
  } else if (score >= 3) {
    strength = 'medium'
  }

  return { isValid: errors.length === 0, errors, strength }
}

// ═══════════════════════════════════════════════════════
// EMAIL & PHONE VALIDATION
// ═══════════════════════════════════════════════════════

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function validatePhone(phone: string): boolean {
  const cleanPhone = phone.replace(/[\s-]/g, '')
  const egyptRegex = /^(\+20|0)?1[0125][0-9]{8}$/
  const uaeRegex = /^(\+971|0)?5[0-9]{8}$/
  return egyptRegex.test(cleanPhone) || uaeRegex.test(cleanPhone)
}

export function isEmailExists(email: string): boolean {
  const userKey = `operiens_user_${email.toLowerCase()}`
  return localStorage.getItem(userKey) !== null
}

export function isPhoneExists(phone: string): boolean {
  const keys = Object.keys(localStorage)
  const userKeys = keys.filter(
    (key) => key.startsWith('operiens_user_') && key !== 'operiens_user',
  )

  for (const key of userKeys) {
    try {
      const userData = localStorage.getItem(key)
      if (userData) {
        const user = JSON.parse(userData)
        const normalizedStored = user.phone?.replace(/[\s-+]/g, '')
        const normalizedInput = phone.replace(/[\s-+]/g, '')
        if (normalizedStored === normalizedInput) {
          return true
        }
      }
    } catch (e) {
      continue
    }
  }
  return false
}

// ═══════════════════════════════════════════════════════
// ENCRYPTION (Enhanced)
// ═══════════════════════════════════════════════════════

export function encryptData(data: string): string {
  const key = 'operines_encrypt_key_2024'
  let encrypted = ''
  for (let i = 0; i < data.length; i++) {
    encrypted += String.fromCharCode(
      data.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    )
  }
  return btoa(encrypted)
}

export function decryptData(encryptedData: string): string {
  const key = 'operines_encrypt_key_2024'
  const decoded = atob(encryptedData)
  let decrypted = ''
  for (let i = 0; i < decoded.length; i++) {
    decrypted += String.fromCharCode(
      decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    )
  }
  return decrypted
}

// ═══════════════════════════════════════════════════════
// PASSWORD GENERATOR
// ═══════════════════════════════════════════════════════

export function generateStrongPassword(): string {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lowercase = 'abcdefghjkmnpqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '@#$%&*!'

  let password = ''
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]

  const allChars = uppercase + lowercase + numbers + symbols
  for (let i = password.length; i < 14; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)]
  }

  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('')
}
